import { useState, useEffect, useRef } from 'react'
import {
  Star,
  Save,
  Send,
  Sparkles,
  AlertTriangle,
  User,
  Calendar,
  Timer,
  CheckCircle,
  FileText,
  Briefcase,
  Clock,
  Target,
  ShieldCheck,
  Mic,
  MicOff,
  Quote,
  Trash2,
  Check,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'

// ── Types ────────────────────────────────────────────────────────────────────

interface Competency {
  id: string
  name: string
  description: string
  weight: number
  field: string
}

const COMPETENCIES: Competency[] = [
  { id: 'communication',   name: 'Communication',   description: 'Clarity, articulation, active listening',          weight: 20, field: 'communication_score' },
  { id: 'technical',       name: 'Technical Skills', description: 'Domain expertise, skill proficiency',              weight: 25, field: 'technical_score' },
  { id: 'problem_solving', name: 'Problem Solving',  description: 'Analytical thinking, approach to challenges',     weight: 20, field: 'problem_solving_score' },
  { id: 'leadership',      name: 'Leadership',       description: 'Initiative, team collaboration, decision-making', weight: 15, field: 'leadership_score' },
  { id: 'cultural_fit',    name: 'Cultural Fit',     description: 'Values alignment, team dynamics',                 weight: 20, field: 'cultural_fit_score' },
]

interface EvaluationData {
  id: number
  session_id: number | null
  booking_id: number | null
  status: string
  communication_score: number | null
  technical_score: number | null
  problem_solving_score: number | null
  leadership_score: number | null
  cultural_fit_score: number | null
  overall_score: number | null
  strengths: string[]
  improvements: string[]
  practice_actions: string[]
  summary: string | null
  observation_notes: string | null
  transcript_excerpts: string[]
  red_flags: string[]
  notes: string | null
  client_name: string | null
  session_title: string | null
  scheduled_at: string | null
}

interface Props {
  evalId: number
  durationMinutes?: number
  onStatusChange?: (newStatus: string) => void
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDateTime(iso: string) {
  const d = new Date(iso)
  return (
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  )
}

function ScoreBar({ score }: { score: number | null }) {
  const pct = score ? (score / 5) * 100 : 0
  const color = score == null ? '#e2e8f0' : score >= 4 ? '#16a34a' : score >= 3 ? '#d97706' : '#dc2626'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 100 }}>
      <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 28, textAlign: 'right' }}>{score ?? '—'}</span>
    </div>
  )
}

// ── List editor ───────────────────────────────────────────────────────────────
// quoteStyle=true → textarea with quote accent (transcript excerpts)
// quoteStyle=false → compact single-line input (strengths, improvements, etc.)

interface ListEditorProps {
  items: string[]
  onChange: (items: string[]) => void
  placeholder: string
  disabled: boolean
  dotColor: string
  addLabel: string
  quoteStyle?: boolean
}

function ListEditor({ items, onChange, placeholder, disabled, dotColor, addLabel, quoteStyle }: ListEditorProps) {
  const update = (i: number, v: string) => { const n = [...items]; n[i] = v; onChange(n) }
  const remove = (i: number) => onChange(items.filter((_, j) => j !== i))
  return (
    <div>
      {items.length === 0 && !disabled && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 8 }}>
          No items yet. Click below to add.
        </p>
      )}
      {items.map((item, i) => (
        <div key={i} className={`efi-list-row ${quoteStyle ? 'efi-list-row--quote' : ''}`}>
          {quoteStyle
            ? <Quote size={13} style={{ color: '#a5b4fc', flexShrink: 0, marginTop: 8 }} />
            : <div className="efi-list-dot" style={{ background: dotColor }} />
          }
          {quoteStyle ? (
            <textarea
              className="form-input efi-quote-input"
              value={item}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              rows={2}
            />
          ) : (
            <input
              type="text"
              className="form-input efi-line-input"
              value={item}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
            />
          )}
          {!disabled && (
            <button className="efi-remove-btn" type="button" onClick={() => remove(i)}>
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button className="efi-add-btn" type="button" onClick={() => onChange([...items, ''])}>
          {addLabel}
        </button>
      )}
    </div>
  )
}

// ── Status Tracker ────────────────────────────────────────────────────────────

const TRACKER_STEPS = [
  { key: 'draft',     label: 'Draft',           desc: 'Evaluation in progress' },
  { key: 'in_review', label: 'In Review',        desc: 'Awaiting approval' },
  { key: 'delivered', label: 'Approved & Sent',  desc: 'Delivered to client' },
]

interface TrackerProps {
  status: string
  aiSummaryApproved: boolean
  approvalChecked: boolean
}

function StatusTracker({ status, aiSummaryApproved, approvalChecked }: TrackerProps) {
  // activeIdx = the step currently highlighted (blue)
  // Use 3 (beyond all steps) when delivered so every step shows green ✓
  const activeIdx =
    status === 'delivered' ? 3 :   // all steps done (green)
    approvalChecked        ? 2 :   // Approval checked → Approved & Sent active
    aiSummaryApproved      ? 1 : 0 // AI summary approved → In Review active

  return (
    <div className="efi-tracker">
      {TRACKER_STEPS.map((step, idx) => {
        const done   = idx < activeIdx
        const active = idx === activeIdx
        return (
          <div key={step.key} className="efi-tracker-item">
            {idx > 0 && (
              <div className={`efi-tracker-line ${done ? 'efi-tracker-line--done' : active ? 'efi-tracker-line--active' : ''}`} />
            )}
            <div className={`efi-tracker-dot ${done ? 'efi-tracker-dot--done' : active ? 'efi-tracker-dot--active' : ''}`}>
              {done ? <Check size={13} /> : <span>{idx + 1}</span>}
            </div>
            <div className={`efi-tracker-label ${active ? 'efi-tracker-label--active' : ''} ${done ? 'efi-tracker-label--done' : ''}`}>
              {step.label}
            </div>
            <div className="efi-tracker-desc">{step.desc}</div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function EvaluationFormInline({ evalId, durationMinutes, onStatusChange }: Props) {
  const [evaluation, setEvaluation]               = useState<EvaluationData | null>(null)
  const [scores, setScores]                       = useState<Record<string, number>>({})
  const [strengths, setStrengths]                 = useState<string[]>([])
  const [improvements, setImprovements]           = useState<string[]>([])
  const [practiceActions, setPracticeActions]     = useState<string[]>([])
  const [summary, setSummary]                     = useState('')
  const [aiSummaryApproved, setAiSummaryApproved] = useState(false)
  const [observationNotes, setObservationNotes]   = useState('')
  const [transcriptExcerpts, setTranscriptExcerpts] = useState<string[]>([])
  const [redFlags, setRedFlags]                   = useState('')
  const [narrativeNotes, setNarrativeNotes]       = useState('')
  const [loading, setLoading]                     = useState(true)
  const [saving, setSaving]                       = useState(false)
  const [generatingAi, setGeneratingAi]           = useState(false)
  const [saveMsg, setSaveMsg]                     = useState('')
  const [approvalChecked, setApprovalChecked]     = useState(false)
  // Voice recording state — 'idle' | 'recording' | 'processing'
  const [voiceObs, setVoiceObs]           = useState<'idle' | 'recording' | 'processing'>('idle')
  const [voiceNarr, setVoiceNarr]         = useState<'idle' | 'recording' | 'processing'>('idle')
  const mediaRecObs  = useRef<MediaRecorder | null>(null)
  const chunksObs    = useRef<Blob[]>([])
  const mediaRecNarr = useRef<MediaRecorder | null>(null)
  const chunksNarr   = useRef<Blob[]>([])
  // AI Phrasing Suggestions state
  interface PhrasingSuggestion { original: string; suggested: string; reason: string }
  const [phrasingSuggestions, setPhrasingSuggestions] = useState<PhrasingSuggestion[]>([])
  const [phrasingTarget, setPhrsingTarget]   = useState<'obs' | 'narr'>('obs')
  const [phrasingLoading, setPhrsingLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    setEvaluation(null)
    setSaveMsg('')
    setApprovalChecked(false)
    setAiSummaryApproved(false)
    evaluationsApi
      .getEvaluation(evalId)
      .then((data: EvaluationData) => {
        setEvaluation(data)
        const loaded: Record<string, number> = {}
        COMPETENCIES.forEach((c) => {
          const v = data[c.field as keyof EvaluationData] as number | null
          if (v != null) loaded[c.id] = v
        })
        setScores(loaded)
        // No defaults — only load what was actually saved
        setStrengths(data.strengths ?? [])
        setImprovements(data.improvements ?? [])
        setPracticeActions(data.practice_actions ?? [])
        setSummary(data.summary ?? '')
        setObservationNotes(data.observation_notes ?? '')
        setTranscriptExcerpts(data.transcript_excerpts ?? [])
        setRedFlags(data.red_flags?.join('\n') ?? '')
        setNarrativeNotes(data.notes ?? '')
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [evalId])

  const buildPayload = () => ({
    ...Object.fromEntries(COMPETENCIES.map((c) => [c.field, scores[c.id] ?? null])),
    strengths:           strengths.filter((s) => s.trim()),
    improvements:        improvements.filter((s) => s.trim()),
    practice_actions:    practiceActions.filter((s) => s.trim()),
    summary,
    observation_notes:   observationNotes || null,
    transcript_excerpts: transcriptExcerpts.filter((s) => s.trim()),
    red_flags:           redFlags.split('\n').map((s) => s.trim()).filter(Boolean),
    notes:               narrativeNotes || null,
  })

  const calcOverall = () => {
    let ws = 0, tw = 0
    COMPETENCIES.forEach((c) => { if (scores[c.id]) { ws += scores[c.id] * c.weight; tw += c.weight } })
    return tw > 0 ? (ws / tw).toFixed(1) : null
  }

  const handleSaveDraft = async () => {
    setSaving(true); setSaveMsg('')
    try {
      const updated = await evaluationsApi.updateEvaluation(evalId, buildPayload())
      setEvaluation((prev) => (prev ? { ...prev, ...updated } : prev))
      setSaveMsg('Saved!')
      setTimeout(() => setSaveMsg(''), 3000)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    setSaving(true)
    try {
      await evaluationsApi.updateEvaluation(evalId, buildPayload())
      const approved = await evaluationsApi.approveEvaluation(evalId)
      const newStatus = approved.status ?? 'delivered'
      setEvaluation((prev) => (prev ? { ...prev, status: newStatus } : prev))
      onStatusChange?.(newStatus)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to approve.')
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateAi = async () => {
    setGeneratingAi(true)
    try {
      // Auto-save current form data first so backend uses the latest values
      await evaluationsApi.updateEvaluation(evalId, buildPayload())
      const res = await evaluationsApi.generateAiSummary(evalId)
      if (res.summary) { setSummary(res.summary); setAiSummaryApproved(false) }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to generate AI summary.')
    } finally {
      setGeneratingAi(false)
    }
  }

  const startRecording = async (
    mediaRecRef: React.MutableRefObject<MediaRecorder | null>,
    chunksRef: React.MutableRefObject<Blob[]>,
    setVoice: (s: 'idle' | 'recording' | 'processing') => void,
    appendText: (t: string) => void,
  ) => {
    // Check browser support
    if (!navigator.mediaDevices?.getUserMedia) {
      alert('Your browser does not support microphone access. Please use Chrome or Firefox.')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err: any) {
      const name = err?.name ?? ''
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        alert(
          'Microphone permission was denied.\n\n' +
          'To fix this:\n' +
          '1. Click the camera/lock icon in your browser address bar\n' +
          '2. Set Microphone to "Allow"\n' +
          '3. Refresh the page and try again'
        )
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.')
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        alert('Microphone is already in use by another application. Please close it and try again.')
      } else {
        alert(`Microphone error: ${err?.message ?? err}`)
      }
      return
    }

    chunksRef.current = []
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4'

    const rec = new MediaRecorder(stream, { mimeType })
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop())
      setVoice('processing')
      try {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        const ext = mimeType.includes('mp4') ? 'mp4' : 'webm'
        const fd = new FormData()
        fd.append('audio', blob, `recording.${ext}`)
        const result = await evaluationsApi.transcribeAudio(fd)
        const text = result?.text ?? ''
        if (text) appendText(text)
      } catch (err: any) {
        alert(`Transcription failed: ${err?.message ?? 'Unknown error'}. Please try again.`)
      } finally {
        setVoice('idle')
      }
    }
    rec.start()
    mediaRecRef.current = rec
    setVoice('recording')
  }

  const stopRecording = (
    mediaRecRef: React.MutableRefObject<MediaRecorder | null>,
  ) => {
    mediaRecRef.current?.stop()
    mediaRecRef.current = null
  }

  const fetchPhrasingSuggestions = async (text: string, target: 'obs' | 'narr') => {
    if (!text.trim()) return
    setPhrsingLoading(true)
    setPhrsingTarget(target)
    setPhrasingSuggestions([])
    try {
      const res = await evaluationsApi.suggestPhrasing(text)
      setPhrasingSuggestions(res.suggestions ?? [])
    } catch {
      // silently fail — phrasing is non-blocking
    } finally {
      setPhrsingLoading(false)
    }
  }

  const applyPhrasingSuggestion = (suggestion: PhrasingSuggestion) => {
    if (phrasingTarget === 'obs') {
      setObservationNotes((prev) => prev.replace(suggestion.original, suggestion.suggested))
    } else {
      setNarrativeNotes((prev) => prev.replace(suggestion.original, suggestion.suggested))
    }
    setPhrasingSuggestions((prev) => prev.filter((s) => s.original !== suggestion.original))
  }

  const handleVoiceObs = () => {
    if (voiceObs === 'recording') {
      stopRecording(mediaRecObs)
    } else if (voiceObs === 'idle') {
      startRecording(
        mediaRecObs, chunksObs, setVoiceObs,
        (t) => {
          setObservationNotes((prev) => {
            const updated = (prev ? prev + ' ' + t : t).trim()
            fetchPhrasingSuggestions(updated, 'obs')
            return updated
          })
        },
      )
    }
  }

  const handleVoiceNarr = () => {
    if (voiceNarr === 'recording') {
      stopRecording(mediaRecNarr)
    } else if (voiceNarr === 'idle') {
      startRecording(
        mediaRecNarr, chunksNarr, setVoiceNarr,
        (t) => {
          setNarrativeNotes((prev) => {
            const updated = (prev ? prev + ' ' + t : t).trim()
            fetchPhrasingSuggestions(updated, 'narr')
            return updated
          })
        },
      )
    }
  }

  // ── States ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading evaluation...</p>
        </div>
      </div>
    )
  }

  if (!evaluation) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <p style={{ color: 'var(--text-muted)' }}>Could not load evaluation.</p>
      </div>
    )
  }

  const isReadOnly = evaluation.status === 'delivered'
  const overall    = calcOverall()
  const statusColor =
    evaluation.status === 'delivered'          ? '#16a34a' :
    evaluation.status === 'interviewer_review' ? '#7c3aed' : '#d97706'
  const statusLabel =
    evaluation.status === 'delivered'          ? 'Delivered' :
    evaluation.status === 'interviewer_review' ? 'Under Review' : 'Draft'

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="efi-form">

      {/* ── 1. Session Details ─────────────────────────────────────────── */}
      <div className="efi-section efi-session-card">
        <div className="efi-section-header">
          <h3 className="efi-section-title">Session Details</h3>
          <span className="efi-status-pill" style={{ background: `${statusColor}18`, color: statusColor }}>
            {evaluation.status === 'delivered' ? <CheckCircle size={12} /> : evaluation.status === 'interviewer_review' ? <FileText size={12} /> : <Clock size={12} />}
            {statusLabel}
          </span>
        </div>
        <div className="efi-session-grid">
          <div className="efi-session-item">
            <User size={15} className="efi-session-icon" />
            <div>
              <div className="efi-session-label">Candidate</div>
              <div className="efi-session-value">{evaluation.client_name || 'Client'}</div>
            </div>
          </div>
          <div className="efi-session-item">
            <Briefcase size={15} className="efi-session-icon" />
            <div>
              <div className="efi-session-label">Role</div>
              <div className="efi-session-value">Interview Candidate</div>
            </div>
          </div>
          {evaluation.scheduled_at && (
            <div className="efi-session-item">
              <Calendar size={15} className="efi-session-icon" />
              <div>
                <div className="efi-session-label">Interview Date</div>
                <div className="efi-session-value">{fmtDateTime(evaluation.scheduled_at)}</div>
              </div>
            </div>
          )}
          {durationMinutes && (
            <div className="efi-session-item">
              <Timer size={15} className="efi-session-icon" />
              <div>
                <div className="efi-session-label">Duration</div>
                <div className="efi-session-value">{durationMinutes} minutes</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Notes & Observations ────────────────────────────────────── */}
      <div className="efi-section efi-obs-card">
        <div className="efi-section-header">
          <h3 className="efi-section-title">
            <FileText size={15} style={{ color: '#0891b2' }} />
            Notes & Observations
          </h3>
          {!isReadOnly && (
            <button
              className={`efi-voice-btn ${voiceObs === 'recording' ? 'efi-voice-btn--recording' : ''}`}
              type="button"
              onClick={handleVoiceObs}
              disabled={voiceObs === 'processing'}
            >
              {voiceObs === 'recording'
                ? <><MicOff size={13} /> Stop Recording</>
                : voiceObs === 'processing'
                  ? <><Sparkles size={13} /> Transcribing...</>
                  : <><Mic size={13} /> Voice Note</>
              }
            </button>
          )}
        </div>
        <p className="efi-obs-hint">
          Supports typed and/or voice notes. Capture human-only insights — presence, body language, nuance.
        </p>
        {voiceObs === 'recording' && (
          <div className="efi-recording-indicator">
            <span className="efi-rec-dot" />
            Recording… speak your observations
          </div>
        )}
        {voiceObs === 'processing' && (
          <div className="efi-recording-indicator efi-recording-indicator--processing">
            <Sparkles size={13} />
            Transcribing with OpenAI Whisper…
          </div>
        )}
        <textarea
          className="form-input efi-textarea"
          placeholder="Type your observations here..."
          value={observationNotes}
          onChange={(e) => setObservationNotes(e.target.value)}
          rows={4}
          disabled={isReadOnly}
        />
        {!isReadOnly && observationNotes.trim() && (
          <button
            type="button"
            className="efi-phrasing-trigger-btn"
            onClick={() => fetchPhrasingSuggestions(observationNotes, 'obs')}
            disabled={phrasingLoading && phrasingTarget === 'obs'}
          >
            <Sparkles size={13} />
            {phrasingLoading && phrasingTarget === 'obs' ? 'Analysing...' : 'AI Phrasing Suggestions'}
          </button>
        )}
        {!observationNotes.trim() && (
          <p className="efi-obs-empty">No notes recorded yet for this session.</p>
        )}

        {/* AI Phrasing Suggestions Panel — Notes & Observations */}
        {phrasingSuggestions.length > 0 && phrasingTarget === 'obs' && (
          <div className="efi-phrasing-panel">
            <div className="efi-phrasing-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={15} style={{ color: '#7c3aed' }} />
                <span className="efi-phrasing-title">AI Phrasing Suggestions</span>
              </div>
              <button type="button" className="efi-phrasing-close" onClick={() => setPhrasingSuggestions([])}>✕</button>
            </div>
            <p className="efi-phrasing-subtitle">Refine your note for clarity and professionalism.</p>
            <div className="efi-phrasing-list">
              {phrasingSuggestions.map((s, i) => (
                <div key={i} className="efi-phrasing-item">
                  <div className="efi-phrasing-original">
                    <span className="efi-phrasing-label">ORIGINAL:</span>
                    <span className="efi-phrasing-original-text">"{s.original}"</span>
                  </div>
                  <div className="efi-phrasing-suggested">
                    <span className="efi-phrasing-label">SUGGESTED:</span>
                    <span className="efi-phrasing-suggested-text">"{s.suggested}"</span>
                  </div>
                  {s.reason && <p className="efi-phrasing-reason"><Sparkles size={11} /> {s.reason}</p>}
                  <button type="button" className="btn btn-primary efi-phrasing-apply" onClick={() => applyPhrasingSuggestion(s)}>
                    <Check size={13} /> Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── 3. Transcript Excerpts ─────────────────────────────────────── */}
      <div className="efi-section efi-transcript-card">
        <div className="efi-section-header">
          <h3 className="efi-section-title">
            <Quote size={15} style={{ color: '#6366f1' }} />
            Transcript Excerpts
          </h3>
          <span className="efi-tag efi-tag--indigo">Key moments from the interview</span>
        </div>
        <p className="efi-obs-hint">Paste notable quotes or key exchanges that support your evaluation.</p>
        <ListEditor
          items={transcriptExcerpts}
          onChange={setTranscriptExcerpts}
          placeholder="Paste a transcript excerpt..."
          disabled={isReadOnly}
          dotColor="#6366f1"
          addLabel="+ Add excerpt"
          quoteStyle
        />
      </div>

      {/* ── 4. Competency Scores ───────────────────────────────────────── */}
      <div className="efi-section">
        <div className="efi-section-header">
          <h3 className="efi-section-title">Competency Scores</h3>
          {overall && (
            <div className="efi-overall">
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Overall Score</span>
              <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary-navy)', lineHeight: 1 }}>
                {overall}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}>/5</span>
              </span>
            </div>
          )}
        </div>
        <div className="efi-scores-list">
          {COMPETENCIES.map((comp) => (
            <div key={comp.id} className="efi-score-row">
              <div className="efi-score-label">
                <span className="efi-score-name">{comp.name}</span>
                <span className="efi-score-desc">{comp.description}</span>
                <span className="efi-score-weight">Weight: {comp.weight}%</span>
              </div>
              <div className="efi-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => !isReadOnly && setScores((p) => ({ ...p, [comp.id]: star }))}
                    disabled={isReadOnly}
                    className={`efi-star ${star <= (scores[comp.id] ?? 0) ? 'efi-star--filled' : ''}`}
                  >
                    <Star size={20} fill={star <= (scores[comp.id] ?? 0) ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
              <ScoreBar score={scores[comp.id] ?? null} />
            </div>
          ))}
        </div>
      </div>

      {/* ── 5. Opportunities for Growth ───────────────────────────────── */}
      <div className="efi-section">
        <h3 className="efi-section-title efi-section-title--improve" style={{ marginBottom: 14 }}>
          Opportunities for Growth
        </h3>
        <ListEditor
          items={improvements}
          onChange={setImprovements}
          placeholder="Add an opportunity for growth..."
          disabled={isReadOnly}
          dotColor="#d97706"
          addLabel="+ Add opportunity"
        />
      </div>

      {/* ── 6. Strengths ──────────────────────────────────────────────── */}
      <div className="efi-section">
        <h3 className="efi-section-title efi-section-title--strength" style={{ marginBottom: 14 }}>
          Strengths
        </h3>
        <ListEditor
          items={strengths}
          onChange={setStrengths}
          placeholder="Add a strength..."
          disabled={isReadOnly}
          dotColor="#16a34a"
          addLabel="+ Add strength"
        />
      </div>

      {/* ── 7. Practice Actions & Next Steps ──────────────────────────── */}
      <div className="efi-section efi-practice-card">
        <div className="efi-section-header" style={{ marginBottom: 14 }}>
          <h3 className="efi-section-title" style={{ color: '#0891b2' }}>
            <Target size={15} style={{ color: '#0891b2' }} />
            Practice Actions & Next Steps
          </h3>
          <span className="efi-tag efi-tag--blue">Shared with candidate</span>
        </div>
        <ListEditor
          items={practiceActions}
          onChange={setPracticeActions}
          placeholder="Add a recommended action or next step..."
          disabled={isReadOnly}
          dotColor="#0891b2"
          addLabel="+ Add action"
        />
      </div>

      {/* ── 8. Red Flags ──────────────────────────────────────────────── */}
      <div className="efi-section efi-redflag-card">
        <div className="efi-section-header">
          <h3 className="efi-section-title" style={{ color: '#dc2626' }}>
            <AlertTriangle size={15} style={{ color: '#dc2626' }} />
            Red Flags
          </h3>
          <span className="efi-tag efi-tag--red">Internal only — not visible to client</span>
        </div>
        <textarea
          className="form-input efi-textarea"
          placeholder="Note any concerns (one per line)..."
          value={redFlags}
          onChange={(e) => setRedFlags(e.target.value)}
          rows={3}
          disabled={isReadOnly}
        />
      </div>

      {/* ── 9. Narrative Assessment ───────────────────────────────────── */}
      <div className="efi-section">
        <div className="efi-section-header">
          <h3 className="efi-section-title">
            <FileText size={15} />
            Narrative Assessment
          </h3>
          {!isReadOnly && (
            <button
              className={`efi-voice-btn ${voiceNarr === 'recording' ? 'efi-voice-btn--recording' : ''}`}
              type="button"
              onClick={handleVoiceNarr}
              disabled={voiceNarr === 'processing'}
            >
              {voiceNarr === 'recording'
                ? <><MicOff size={13} /> Stop Recording</>
                : voiceNarr === 'processing'
                  ? <><Sparkles size={13} /> Transcribing...</>
                  : <><Mic size={13} /> Voice Note</>
              }
            </button>
          )}
        </div>
        {voiceNarr === 'recording' && (
          <div className="efi-recording-indicator">
            <span className="efi-rec-dot" />
            Recording… dictate your narrative assessment
          </div>
        )}
        {voiceNarr === 'processing' && (
          <div className="efi-recording-indicator efi-recording-indicator--processing">
            <Sparkles size={13} />
            Transcribing with OpenAI Whisper…
          </div>
        )}
        <textarea
          className="form-input efi-textarea"
          placeholder="Write a detailed narrative assessment of the candidate's overall performance..."
          value={narrativeNotes}
          onChange={(e) => setNarrativeNotes(e.target.value)}
          rows={4}
          disabled={isReadOnly}
        />
        {!isReadOnly && narrativeNotes.trim() && (
          <button
            type="button"
            className="efi-phrasing-trigger-btn"
            onClick={() => fetchPhrasingSuggestions(narrativeNotes, 'narr')}
            disabled={phrasingLoading && phrasingTarget === 'narr'}
          >
            <Sparkles size={13} />
            {phrasingLoading && phrasingTarget === 'narr' ? 'Analysing...' : 'AI Phrasing Suggestions'}
          </button>
        )}

        {/* AI Phrasing Suggestions Panel — Narrative Assessment */}
        {phrasingSuggestions.length > 0 && phrasingTarget === 'narr' && (
          <div className="efi-phrasing-panel">
            <div className="efi-phrasing-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={15} style={{ color: '#7c3aed' }} />
                <span className="efi-phrasing-title">AI Phrasing Suggestions</span>
              </div>
              <button type="button" className="efi-phrasing-close" onClick={() => setPhrasingSuggestions([])}>✕</button>
            </div>
            <p className="efi-phrasing-subtitle">Refine your note for clarity and professionalism.</p>
            <div className="efi-phrasing-list">
              {phrasingSuggestions.map((s, i) => (
                <div key={i} className="efi-phrasing-item">
                  <div className="efi-phrasing-original">
                    <span className="efi-phrasing-label">ORIGINAL:</span>
                    <span className="efi-phrasing-original-text">"{s.original}"</span>
                  </div>
                  <div className="efi-phrasing-suggested">
                    <span className="efi-phrasing-label">SUGGESTED:</span>
                    <span className="efi-phrasing-suggested-text">"{s.suggested}"</span>
                  </div>
                  {s.reason && <p className="efi-phrasing-reason"><Sparkles size={11} /> {s.reason}</p>}
                  <button type="button" className="btn btn-primary efi-phrasing-apply" onClick={() => applyPhrasingSuggestion(s)}>
                    <Check size={13} /> Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── 10. AI-Generated Summary ───────────────────────────────────── */}
      <div className="efi-section efi-ai-card">
        <div className="efi-section-header">
          <h3 className="efi-section-title">
            <Sparkles size={16} style={{ color: '#7c3aed' }} />
            AI-Generated Summary
          </h3>
          {!isReadOnly && (
            <button className="efi-ai-btn" onClick={handleGenerateAi} disabled={generatingAi}>
              <Sparkles size={13} />
              {generatingAi ? 'Generating...' : summary ? 'Regenerate' : 'Generate'}
            </button>
          )}
        </div>

        {summary ? (
          <div className="efi-ai-summary-box">
            <div className="efi-ai-summary-icon">
              <Sparkles size={14} style={{ color: '#7c3aed' }} />
            </div>
            <p className="efi-ai-summary-text">{summary}</p>
          </div>
        ) : (
          <div className="efi-ai-empty">
            <Sparkles size={20} style={{ color: '#c4b5fd' }} />
            <p>Click <strong>Generate</strong> to create one from interview data. It's not editable.</p>
          </div>
        )}

        <label className="efi-ai-approve-row">
          <input
            type="checkbox"
            className="efi-checkbox"
            checked={aiSummaryApproved}
            onChange={(e) => setAiSummaryApproved(e.target.checked)}
            disabled={!summary.trim() || isReadOnly}
          />
          <span className="efi-ai-approve-text">
            I have reviewed and approve this AI summary for the client report
          </span>
          {aiSummaryApproved && (
            <span className="efi-ai-approved-badge">
              <Check size={11} /> Approved
            </span>
          )}
        </label>
      </div>

      {/* ── 11. Approval Required Before Release ──────────────────────── */}
      {!isReadOnly ? (
        <div className="efi-approval-card">
          <div className="efi-approval-header">
            <ShieldCheck size={20} style={{ color: '#b45309', flexShrink: 0 }} />
            <div>
              <h3 className="efi-approval-title">Approval Required Before Release</h3>
              <p className="efi-approval-desc">
                Review the merged report carefully. Once approved, it will be released to the client.
              </p>
            </div>
          </div>
          <label className="efi-approval-check-wrap">
            <div className="efi-approval-check-inner">
              <input
                type="checkbox"
                className="efi-checkbox"
                checked={approvalChecked}
                onChange={(e) => setApprovalChecked(e.target.checked)}
              />
              <span className="efi-approval-check-text">
                I have reviewed all sections and confirm this report accurately reflects the interview assessment
              </span>
              {approvalChecked && (
                <span className="efi-approved-inline">
                  <Check size={11} /> Confirmed
                </span>
              )}
            </div>
          </label>
        </div>
      ) : (
        <div className="efi-delivered-notice">
          <CheckCircle size={16} style={{ color: '#16a34a' }} />
          This evaluation has been delivered to the client.
        </div>
      )}

      {/* ── 12. Evaluation Progress + Action Buttons ──────────────────── */}
      <div className="efi-section efi-tracker-card">
        <h3 className="efi-section-title" style={{ marginBottom: 20 }}>Evaluation Progress</h3>
        <StatusTracker
          status={evaluation.status}
          aiSummaryApproved={aiSummaryApproved}
          approvalChecked={approvalChecked}
        />

        {!isReadOnly && (
          <div className="efi-tracker-actions">
            {saveMsg && <span className="efi-save-msg">✓ {saveMsg}</span>}
            <button className="btn btn-secondary" type="button" onClick={handleSaveDraft} disabled={saving}>
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              className="btn btn-success efi-approve-btn"
              type="button"
              onClick={handleApprove}
              disabled={saving || !approvalChecked}
              title={!approvalChecked ? 'Check the confirmation box above to enable approval' : ''}
            >
              <Send size={16} />
              Approve & Send to Client
            </button>
          </div>
        )}
      </div>

    </div>
  )
}
